import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {SET_GUEST_OPTIONS} from '~/common/fabric/reducers/guestOptions/constants';
import {
  selectRSVPDeadline,
  selectRsvpDeadlineIsStrict,
} from '~/common/fabric/reducers/guestOptions/selectors';
import {Button} from '~library/atoms/Button';
import {GuestOptionMaxGuests} from './components/GuestOptionMaxGuests';
import {GuestOptionPlusOnes} from './components/GuestOptionPlusOnes';
import {GuestOptionToggle} from './components/GuestOptionToggle';
import {RSVPDeadlinePicker} from './components/RSVPDeadlinePicker';
import {RsvpToggle} from './components/RsvpToggle';

import './GuestOptionsForm.sass';

interface GuestOptionsFormProps {
  options: {
    hideGuestlist: boolean;
    allowBringPeople: boolean;
    limitPerGuest: number;
    maxGuestList: number | null;
    showMaxGuest: boolean;
    allowKids: boolean;
    allowMaybeRSVPs: boolean;
    rsvpOff: boolean;
    loading: boolean;
  };
  setOptions: React.Dispatch<React.SetStateAction<GuestOptionsFormProps['options']>>;
  handleSave: () => void;
  isGuestOptionsPage: boolean;
}

export const GuestOptionsForm: React.FC<GuestOptionsFormProps> = ({
  options,
  setOptions,
  handleSave,
  isGuestOptionsPage,
}) => {
  const {
    hideGuestlist,
    allowBringPeople,
    limitPerGuest,
    maxGuestList,
    showMaxGuest,
    allowKids,
    allowMaybeRSVPs,
    rsvpOff,
    loading,
  } = options;

  const dispatch = useDispatch();
  const rsvpDeadline = useSelector(selectRSVPDeadline);
  const rsvpDeadlineIsStrict = useSelector(selectRsvpDeadlineIsStrict);

  const [showRsvpDeadline, setShowRsvpDeadline] = useState(rsvpDeadline || false);

  // On first render, ensure the toggle is on if rsvpDeadline exists
  useEffect(() => {
    setShowRsvpDeadline(rsvpDeadline);
  }, [rsvpDeadline]);

  const handleToggleRsvpDeadline = () => {
    // biome-ignore lint/suspicious/noExplicitAny: allow any
    setShowRsvpDeadline((prev: any) => {
      const newValue = !prev;
      if (!newValue) {
        // If toggling off, clear the global state values
        dispatch({
          type: SET_GUEST_OPTIONS,
          payload: {
            rsvpDeadline: undefined,
            rsvpDeadlineIsStrict: undefined,
          },
        });
      } else {
        dispatch({
          type: SET_GUEST_OPTIONS,
          payload: {
            rsvpDeadlineIsStrict: true,
          },
        });
      }
      return newValue;
    });
  };

  const maxGuestListSize = 5000;

  return (
    <div className="guest-options-form guest-options-form__content">
      <div className="guest-options-form__content__main">
        <div className="guest-options-form__toggles">
          {/* Collect RSVPs */}
          <RsvpToggle
            rsvpOff={rsvpOff}
            onChange={() => setOptions((prev) => ({...prev, rsvpOff: !rsvpOff}))}
          />

          {/* RSVP Deadline */}
          <div className="rsvp-deadline__wrapper">
            <GuestOptionToggle
              checked={!!showRsvpDeadline && !rsvpOff}
              heading="RSVP Deadline"
              description="Set a reply-by date and select if guests can still RSVP after the deadline."
              onChange={handleToggleRsvpDeadline}
              dataQaId="rsvp-deadline-toggle"
              disabled={rsvpOff}
              footerContent={
                <RSVPDeadlinePicker
                  rsvpDeadline={rsvpDeadline}
                  rsvpDeadlineIsStrict={rsvpDeadlineIsStrict}
                  isGuestOptionsPage={isGuestOptionsPage}
                />
              }
            />
          </div>

          {/* Maybe RSVPs */}
          <GuestOptionToggle
            checked={allowMaybeRSVPs}
            heading='Allow "Maybe" RSVPs'
            description="For the guests with commitment issues."
            onChange={() =>
              setOptions((prev) => ({
                ...prev,
                allowMaybeRSVPs: !allowMaybeRSVPs,
              }))
            }
            dataQaId="allow-maybe-toggle"
            disabled={rsvpOff}
          />

          {/* Private guest list */}
          <GuestOptionToggle
            checked={!rsvpOff && hideGuestlist}
            heading="Private guest list"
            description="Only the host can see the guest list."
            onChange={() => setOptions((prev) => ({...prev, hideGuestlist: !hideGuestlist}))}
            dataQaId="hide-guest-list"
            disabled={rsvpOff}
          />

          {/* Plus ones */}
          <GuestOptionPlusOnes
            checked={allowBringPeople}
            heading="Plus ones"
            description="Specify how many additional people each guest can bring."
            onChange={() =>
              setOptions((prev) => ({
                ...prev,
                allowBringPeople: !allowBringPeople,
              }))
            }
            limitPerGuest={limitPerGuest}
            setLimitPerGuest={(value) => setOptions((prev) => ({...prev, limitPerGuest: value}))}
            dataQaId="allow-bring-people"
            disabled={rsvpOff}
          />

          {/* Family headcount */}
          <GuestOptionToggle
            checked={!rsvpOff && allowKids}
            heading="Family headcount"
            description="Find out how many adults & kids are coming."
            onChange={() => setOptions((prev) => ({...prev, allowKids: !allowKids}))}
            dataQaId="allow-kids"
            disabled={(!allowBringPeople && limitPerGuest <= 0) || rsvpOff}
          />

          {/* Max event capacity */}
          <GuestOptionMaxGuests
            checked={showMaxGuest}
            maxGuestList={maxGuestList}
            setMaxGuestList={(value: number | null) =>
              setOptions((prev) => ({...prev, maxGuestList: value}))
            }
            showMaxGuest={showMaxGuest}
            setShowMaxGuest={(value) =>
              setOptions((prev) => ({
                ...prev,
                showMaxGuest: typeof value === 'function' ? value(prev.showMaxGuest) : value,
              }))
            }
            disabled={rsvpOff}
          />
        </div>
      </div>

      <div className="guest-options-form__footer">
        <Button
          className="guest-options-form__save-button"
          data-qa-id="save-guest-options"
          variant="primary"
          size="large"
          onClick={handleSave}
          disabled={loading || (maxGuestList !== null && maxGuestList > maxGuestListSize)}
          fullWidth
        >
          Save
        </Button>
      </div>
    </div>
  );
};
